import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  ApplyWorkOrderMergeRequestDto,
  AssignWorkOrderRequestDto,
  CompleteWorkOrderRequestDto,
  HoldWorkOrderRequestDto,
  MoveTicketRequestDto,
} from '@evimo/client/models';
import { MaintenanceExecutionService } from '~/app/services/maintenance-execution/maintenance-execution.service';
import { workOrderBoardQueryKey } from '~/app/hooks/maintenance-execution/useWorkOrderBoard';

export function useWorkOrderMutations(organizationId: string) {
  const queryClient = useQueryClient();

  const invalidateBoard = () =>
    queryClient.invalidateQueries({
      queryKey: workOrderBoardQueryKey(organizationId),
    });

  const invalidateTicketHistory = () =>
    queryClient.invalidateQueries({
      queryKey: ['maintenance-execution', 'ticket'],
    });

  const invalidateWorkOrderDetails = () =>
    queryClient.invalidateQueries({
      queryKey: ['maintenance-execution', 'work-order'],
    });

  const invalidateAfterBoardChange = async () => {
    await Promise.all([
      invalidateBoard(),
      invalidateTicketHistory(),
      invalidateWorkOrderDetails(),
    ]);
  };

  const invalidateSuggestions = () =>
    queryClient.invalidateQueries({
      queryKey: [
        'maintenance-execution',
        'optimizer-suggestions',
        organizationId,
      ],
    });

  const assign = useMutation({
    mutationFn: (input: {
      workOrderId: string;
      body: AssignWorkOrderRequestDto;
    }) => MaintenanceExecutionService.assign(input.workOrderId, input.body),
    onSuccess: invalidateAfterBoardChange,
  });

  const start = useMutation({
    mutationFn: (workOrderId: string) =>
      MaintenanceExecutionService.start(workOrderId),
    onSuccess: invalidateAfterBoardChange,
  });

  const hold = useMutation({
    mutationFn: (input: {
      workOrderId: string;
      body: HoldWorkOrderRequestDto;
    }) => MaintenanceExecutionService.hold(input.workOrderId, input.body),
    onSuccess: invalidateAfterBoardChange,
  });

  const closeWorkOrder = useMutation({
    mutationFn: (workOrderId: string) =>
      MaintenanceExecutionService.closeWorkOrder(workOrderId, organizationId),
    onSuccess: invalidateAfterBoardChange,
  });

  const closeIntervention = useMutation({
    mutationFn: (input: { workOrderId: string; ticketId: string }) =>
      MaintenanceExecutionService.closeIntervention(
        input.workOrderId,
        input.ticketId,
        organizationId,
      ),
    onSuccess: invalidateAfterBoardChange,
  });

  const complete = useMutation({
    mutationFn: (input: {
      workOrderId: string;
      body: CompleteWorkOrderRequestDto;
    }) => MaintenanceExecutionService.complete(input.workOrderId, input.body),
    onSuccess: invalidateAfterBoardChange,
  });

  const moveTicket = useMutation({
    mutationFn: (input: {
      sourceWorkOrderId: string;
      ticketId: string;
      body: MoveTicketRequestDto;
    }) =>
      MaintenanceExecutionService.moveTicket(
        input.sourceWorkOrderId,
        input.ticketId,
        input.body,
      ),
    onSuccess: invalidateAfterBoardChange,
  });

  const applyMerge = useMutation({
    mutationFn: (body: ApplyWorkOrderMergeRequestDto) =>
      MaintenanceExecutionService.applyMerge(body, organizationId),
    onSuccess: async () => {
      await Promise.all([
        invalidateAfterBoardChange(),
        invalidateSuggestions(),
      ]);
    },
  });

  return {
    assign,
    start,
    hold,
    complete,
    closeWorkOrder,
    closeIntervention,
    moveTicket,
    applyMerge,
  };
}
